import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { PrevistoLinhaResponse } from '@triade/contracts';
import { ChevronLeft, ChevronRight, ListChecks } from 'lucide-react';
import { useCopiarPrevisto, usePrevisto, useResumoMes, useSalvarPrevisto } from '@/api/plano';
import { usePode } from '@/api/me';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';
import { formatarMoeda } from '@/lib/format';
import { useSessao } from '@/lib/session';
import { deslocarMes, type LinhaRascunho, mesesDeOrigem, montarPrevisto, ROTULO_CLASSE, rascunhoDe, rotuloMes } from './custo-fabrica-mes';
import { RubricasDialog } from './rubricas-dialog';
import { UsaAlimenta } from './usa-alimenta';
import { BotaoRelatorios } from '@/relatorios';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';

export { lerBuscaCustoFabrica } from './custo-fabrica-mes';

const CAMPO = 'h-[34px] w-full rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body tabular-nums focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-70';
const CAMPO_DATA = 'h-[34px] rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-70';
const BOTAO_MES = 'flex size-[34px] items-center justify-center rounded-[9px] border border-border bg-card hover:bg-line-soft';

const mensagemApi = (e: unknown) => (e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');

/**
 * Custo de fabrica do mes (/plano/custo-fabrica): previsto por rubrica, total da API e resultado do mes dos dois jeitos.
 * Nenhum valor e calculado aqui: total, resultados e rubricas de caixa vem prontos da API (ausentes = "—").
 */
export function CustoFabricaPage({ mes }: { mes: string }) {
  const { empresaId } = useSessao();
  const navigate = useNavigate();
  const { pode } = usePode();
  const podeEditar = pode('plano.editar');
  const mudarMes = (novo: string) => void navigate({ to: '/plano/custo-fabrica', search: { mes: novo }, replace: true });

  return (
    <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-small text-ink-muted">
            Plano <span aria-hidden="true">/</span> Custo fábrica
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Custo fábrica</h1>
          <p className="text-small text-ink-secondary">Previsto mensal por rubrica e resultado do mês</p>
          <UsaAlimenta usa={[{ rotulo: 'Carteira', to: '/plano/carteira' }]} alimenta={[{ rotulo: 'Fluxo de caixa', to: '/plano/fluxo' }]} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <BotaoRelatorios tela="plano-custo-fabrica" params={{ mes }} />
        <nav aria-label="Mês do custo de fábrica" className="flex items-center gap-2">
          <button type="button" aria-label="Mês anterior" className={BOTAO_MES} onClick={() => mudarMes(deslocarMes(mes, -1))}>
            <ChevronLeft aria-hidden="true" className="size-4" />
          </button>
          <span aria-live="polite" className="min-w-[90px] text-center text-body font-semibold tabular-nums" data-testid="mes-custo-fabrica">
            {rotuloMes(mes)}
          </span>
          <button type="button" aria-label="Próximo mês" className={BOTAO_MES} onClick={() => mudarMes(deslocarMes(mes, 1))}>
            <ChevronRight aria-hidden="true" className="size-4" />
          </button>
        </nav>
        </div>
      </div>

      {empresaId === null ? (
        <p role="status" className="px-5 py-10 text-center text-small text-ink-muted">Escolha a empresa no menu lateral para ver o custo fábrica.</p>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
          <PrevistoMes mes={mes} podeEditar={podeEditar} />
          <ResultadoMes mes={mes} />
        </div>
      )}
    </div>
  );
}

// ---------- Previsto do mes ----------

function PrevistoMes({ mes, podeEditar }: { mes: string; podeEditar: boolean }) {
  const previsto = usePrevisto(mes);
  const salvar = useSalvarPrevisto(mes);
  const copiar = useCopiarPrevisto(mes);
  const [rascunho, setRascunho] = useState<Record<string, LinhaRascunho>>({});
  const [origem, setOrigem] = useState('');
  const [gerindo, setGerindo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const linhas = previsto.data?.linhas;

  // Ao chegar (ou trocar) o previsto da API, o rascunho volta aos valores salvos.
  useEffect(() => {
    if (linhas) setRascunho(rascunhoDe(linhas));
  }, [linhas]);

  function alterar(rubricaId: string, campo: keyof LinhaRascunho, valor: string) {
    setRascunho((atual) => ({ ...atual, [rubricaId]: { ...(atual[rubricaId] ?? { valor: '', dataPagamento: '' }), [campo]: valor } }));
  }

  async function salvarPrevisto() {
    if (!linhas) return;
    setErro(null);
    setAviso(null);
    const montagem = montarPrevisto(linhas, rascunho);
    if (!montagem.ok) {
      setErro(montagem.mensagem);
      return;
    }
    try {
      await salvar.mutateAsync(montagem.dto);
      setAviso('Previsto salvo.');
    } catch (e) {
      setErro(mensagemApi(e));
    }
  }

  async function copiarDe() {
    if (!origem) return;
    setErro(null);
    setAviso(null);
    try {
      await copiar.mutateAsync({ origem });
      setAviso(`Previsto copiado de ${rotuloMes(origem)}.`);
    } catch (e) {
      setErro(mensagemApi(e));
    }
  }

  let corpo: ReactNode;
  if (previsto.isError && !previsto.data) corpo = <div className="p-5"><ErroFormacao erro={previsto.error} onTentar={() => void previsto.refetch()} titulo="Não foi possível carregar o previsto" /></div>;
  else if (!linhas) corpo = <p role="status" className="p-5 text-small text-ink-muted">Carregando previsto…</p>;
  else if (linhas.length === 0) corpo = <p role="status" className="p-5 text-small text-ink-muted">Nenhuma rubrica ativa. Crie as rubricas para lançar o previsto.</p>;
  else corpo = <TabelaPrevisto linhas={linhas} rascunho={rascunho} podeEditar={podeEditar} onAlterar={alterar} total={previsto.data?.total} />;

  return (
    <section aria-label="Previsto do mês" className="flex min-w-0 flex-col rounded-2xl border border-border bg-card">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3.5">
        <h2 className="text-body font-semibold">Previsto do mês</h2>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" onClick={() => setGerindo(true)} className="h-[34px] gap-1.5 rounded-[9px] px-3">
            <ListChecks aria-hidden="true" className="size-[15px]" />
            Rubricas
          </Button>
        </div>
      </div>

      {corpo}

      {podeEditar && linhas && linhas.length > 0 && (
        <div className="flex flex-wrap items-end gap-2 border-t border-border px-4 py-3">
          <div className="flex flex-col gap-1">
            <label htmlFor="copiar-previsto-origem" className="text-caption font-medium text-ink-secondary">Copiar de outro mês</label>
            <select id="copiar-previsto-origem" className={CAMPO_DATA} value={origem} onChange={(e) => setOrigem(e.target.value)}>
              <option value="">Escolha o mês</option>
              {mesesDeOrigem(mes).map((m) => (
                <option key={m} value={m}>{rotuloMes(m)}</option>
              ))}
            </select>
          </div>
          <Button type="button" variant="outline" disabled={!origem || copiar.isPending} onClick={() => void copiarDe()} className="h-[34px] rounded-[9px] px-3">
            {copiar.isPending ? 'Copiando…' : 'Copiar'}
          </Button>
          <Button type="button" disabled={salvar.isPending} onClick={() => void salvarPrevisto()} className="ml-auto h-[34px] rounded-[10px] px-4">
            {salvar.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      )}
      {erro && <p role="alert" className="px-4 pb-3 text-small text-danger-ink">{erro}</p>}
      {aviso && <p role="status" className="px-4 pb-3 text-small text-ink-secondary">{aviso}</p>}

      {gerindo && <RubricasDialog podeEditar={podeEditar} onFechar={() => setGerindo(false)} />}
    </section>
  );
}

function TabelaPrevisto({
  linhas,
  rascunho,
  podeEditar,
  onAlterar,
  total,
}: {
  linhas: PrevistoLinhaResponse[];
  rascunho: Record<string, LinhaRascunho>;
  podeEditar: boolean;
  onAlterar: (rubricaId: string, campo: keyof LinhaRascunho, valor: string) => void;
  total: string | undefined;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[560px] border-collapse text-small" aria-label="Previsto por rubrica">
        <thead>
          <tr className="text-left text-caption text-ink-muted">
            <th scope="col" className="px-4 py-2.5 font-medium">Rubrica</th>
            <th scope="col" className="px-3 py-2.5 font-medium">Classe</th>
            <th scope="col" className="w-[160px] px-3 py-2.5 text-right font-medium">Valor previsto</th>
            <th scope="col" className="w-[160px] px-3 py-2.5 font-medium">Pagamento</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l) => {
            const r = rascunho[l.rubricaId] ?? { valor: '', dataPagamento: '' };
            return (
              <tr key={l.rubricaId} data-testid="linha-rubrica" className="border-t border-line-soft">
                <td className="px-4 py-2.5">{l.nome}</td>
                <td className="px-3 py-2.5 text-ink-secondary">{ROTULO_CLASSE[l.classe]}</td>
                <td className="px-3 py-2.5">
                  <input
                    aria-label={`Valor previsto de ${l.nome}`}
                    inputMode="decimal"
                    autoComplete="off"
                    className={CAMPO}
                    value={r.valor}
                    disabled={!podeEditar}
                    placeholder="—"
                    onChange={(e) => onAlterar(l.rubricaId, 'valor', e.target.value)}
                  />
                </td>
                <td className="px-3 py-2.5">
                  <input
                    type="date"
                    aria-label={`Data de pagamento de ${l.nome}`}
                    className={CAMPO_DATA}
                    value={r.dataPagamento}
                    disabled={!podeEditar}
                    onChange={(e) => onAlterar(l.rubricaId, 'dataPagamento', e.target.value)}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t border-border font-semibold">
            <th scope="row" colSpan={2} className="px-4 py-3 text-left">Total do mês (custo fábrica)</th>
            <td colSpan={2} className="px-3 py-3 text-right tabular-nums" data-testid="total-previsto">{formatarMoeda(total)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ---------- Resultado do mes ----------

function ResultadoMes({ mes }: { mes: string }) {
  const resumo = useResumoMes(mes);
  const dados = resumo.data;

  return (
    <section aria-label="Resultado do mês" className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-4">
      <h2 className="text-body font-semibold">Resultado do mês</h2>
      {resumo.isError && !dados ? (
        <ErroFormacao erro={resumo.error} onTentar={() => void resumo.refetch()} titulo="Não foi possível carregar o resultado" />
      ) : !dados ? (
        <p role="status" className="text-small text-ink-muted">Carregando resultado…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-[10px] border border-border p-3">
              <div className="text-caption font-medium text-ink-secondary">Como a planilha</div>
              <div className="mt-1 text-body font-semibold tabular-nums" data-testid="resultado-planilha">{formatarMoeda(dados.resultadoPlanilha)}</div>
            </div>
            <div className="rounded-[10px] border border-border p-3">
              <div className="text-caption font-medium text-ink-secondary">Gerencial</div>
              <div className="mt-1 text-body font-semibold tabular-nums" data-testid="resultado-gerencial">{formatarMoeda(dados.resultadoGerencial)}</div>
            </div>
          </div>
          <div className="flex items-center justify-between text-small">
            <span className="text-ink-secondary">Rubricas de caixa</span>
            <span className="tabular-nums" data-testid="rubricas-caixa">{formatarMoeda(dados.rubricasDeCaixa)}</span>
          </div>
          <p className="text-caption text-ink-muted">As rubricas de caixa saem do resultado gerencial e vão só para o fluxo de caixa.</p>
        </>
      )}
    </section>
  );
}
